'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { hospitalConfig } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';

export default function EmergencyPage() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    trackEvent({ event: 'emergency_clicked', source: 'emergency-page' });
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      className="min-h-screen flex flex-col bg-red-950"
      role="main"
      aria-label="Emergency — All Is Well Hospital"
      style={{ background: 'linear-gradient(135deg, #1a0000 0%, #2d0000 50%, #1a0000 100%)' }}
    >
      {/* Top bar */}
      <nav
        className="flex items-center justify-between px-6 py-4 border-b border-red-900"
        role="navigation"
        aria-label="Emergency navigation"
      >
        <Link href="/" aria-label="Return to All Is Well Experience home">
          <div className="logo-pill py-1 px-3">
            <AppImage
              src={hospitalConfig?.logo}
              alt="All Is Well Multi-Speciality Hospital Logo"
              width={80}
              height={32}
              className="h-8 w-auto object-contain"
              priority
            />
          </div>
        </Link>
        <Link
          href="/"
          className="text-xs text-red-300 hover:text-red-100 transition-colors font-medium"
          aria-label="Return to home"
        >
          ← Back to Home
        </Link>
      </nav>
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div
          className={`w-full max-w-lg text-center transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Alert icon */}
          <div
            className="text-6xl mb-6 animate-emergency-pulse inline-block"
            aria-hidden="true"
          >
            🚨
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-red-300 mb-4 leading-tight tracking-wide">
            THIS MAY REQUIRE IMMEDIATE MEDICAL ATTENTION
          </h1>

          <p className="text-sm text-red-200/80 mb-8 leading-relaxed max-w-sm mx-auto">
            If you believe this is a medical emergency, seek immediate emergency medical care.
            Do not wait for online guidance.
          </p>

          {/* Primary emergency actions */}
          <div className="flex flex-col gap-4 mb-8">
            <a
              href={hospitalConfig?.emergencyTel}
              onClick={() => trackEvent({ event: 'call_clicked', source: 'emergency-page' })}
              className="btn-emergency w-full text-base tracking-widest py-5 animate-emergency-pulse"
              aria-label={`Call All Is Well Hospital Emergency: ${hospitalConfig?.emergency}`}
            >
              📞 CALL EMERGENCY NOW — {hospitalConfig?.emergency}
            </a>
            <a
              href={hospitalConfig?.maps}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-xl border border-red-700 text-red-300 hover:bg-red-900/30 transition-all duration-200 py-4 text-sm font-bold tracking-widest flex items-center justify-center gap-2 min-h-[56px]"
              aria-label="Get directions to All Is Well Hospital"
            >
              📍 GET DIRECTIONS TO HOSPITAL
            </a>
            <a
              href={hospitalConfig?.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent({ event: 'whatsapp_clicked', source: 'emergency-page' })}
              className="w-full rounded-xl border border-red-800/50 text-red-400 hover:bg-red-900/20 transition-all duration-200 py-4 text-sm font-bold tracking-widest flex items-center justify-center gap-2 min-h-[56px]"
              aria-label="WhatsApp All Is Well Hospital"
            >
              💬 WHATSAPP
            </a>
          </div>

          {/* Emergency symptoms */}
          <div
            className="border border-red-900/60 rounded-2xl p-5 mb-6 text-left"
            role="note"
            aria-label="Emergency warning symptoms"
          >
            <h2 className="text-xs font-bold text-red-400 tracking-widest uppercase mb-3">
              Seek Immediate Care For
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: 'sym-chest', text: 'Severe chest pain' },
                { id: 'sym-breathing', text: 'Severe breathing difficulty' },
                { id: 'sym-unconscious', text: 'Loss of consciousness' },
                { id: 'sym-trauma', text: 'Major trauma or injury' },
                { id: 'sym-bleeding', text: 'Severe or uncontrolled bleeding' },
                { id: 'sym-stroke', text: 'Stroke-like symptoms' },
                { id: 'sym-seizure', text: 'Seizure or convulsion' },
                { id: 'sym-deterioration', text: 'Sudden severe deterioration' },
              ]?.map((sym) => (
                <div key={sym?.id} className="flex items-center gap-2 text-sm text-red-200/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" aria-hidden="true" />
                  {sym?.text}
                </div>
              ))}
            </div>
          </div>

          {/* Hospital address */}
          <div className="text-center mb-6">
            <p className="text-xs text-red-400/70">
              📍 {hospitalConfig?.address}
            </p>
          </div>

          <Link
            href="/"
            className="text-xs text-red-400/60 hover:text-red-300 transition-colors underline"
            aria-label="Return to All Is Well Experience — this is not an emergency"
          >
            This is not an emergency — return to home
          </Link>
        </div>
      </div>
    </main>
  );
}